/**
 * How a Feed reaches its pages, as a control.
 *
 * The four modes are the Fetcher's, unchanged — this is a form over behaviour
 * that already existed and could only be reached by patching the API by hand.
 * It lives in one component because the choice is offered twice, when a source
 * is added and when one is edited, and the two must not drift: a mode that meant
 * something different on the second screen would be worse than no control.
 *
 * `auto` is worded as a default rather than as a recommendation, because it is a
 * good default and a bad guarantee: following a published link is not
 * automatically safe, and `pff-ingestion/13` is where that is being decided.
 */

import type { Pagination, PaginationMode } from "../api";
import { Field, Select, SelectCompact, TextInput, TextInputCompact } from "./ui";

const MODES: { value: PaginationMode; label: string }[] = [
  { value: "auto", label: "Automatic (default)" },
  { value: "none", label: "One page only" },
  { value: "page_param", label: "Count pages with a parameter" },
  { value: "page_until_short", label: "Count pages until one comes back short" },
];

const HINTS: Record<PaginationMode, string> = {
  auto: "Follow a next link the source publishes, otherwise count pages it publishes, otherwise read one page.",
  none: "Read one page and do not look for another, even if the source advertises one.",
  page_param:
    "Add one to a query parameter on the address that answered, and stop when the source says there are no more pages. Use this where the published next link goes somewhere the portal cannot follow.",
  page_until_short:
    "Add one to a query parameter and keep going until a page returns fewer records than a full page holds. Use this where the source publishes nothing outside its records — no next link, no page count — so there is nothing else to read.",
};

export const DEFAULT_PAGINATION: Pagination = { mode: "auto", param: null };

/**
 * A stored mode the portal does not support stays visible and selected.
 *
 * Without it the select falls to its first option, so a Feed hand-patched with
 * something outside the three would read as *Automatic* — and the next save
 * would quietly make it so, which is the reverse of showing someone that their
 * Feed says something the Fetcher cannot honour. Saving is then refused by the
 * API until one of the three is chosen, which is the point: the value was never
 * one the Fetcher read. The Sport select on the same screen keeps a withdrawn
 * sport for the same reason.
 */
function optionsFor(mode: PaginationMode): { value: PaginationMode; label: string }[] {
  if (MODES.some((known) => known.value === mode)) return MODES;
  return [{ value: mode, label: `${mode} (not a pagination mode the portal supports)` }, ...MODES];
}

/** The stacked form, for a screen with room for a hint. */
export function PaginationChoice({
  value,
  onChange,
  label = "Pagination",
}: {
  value: Pagination;
  onChange: (value: Pagination) => void;
  label?: string;
}) {
  return (
    <div className="flex flex-col gap-md fold:flex-row fold:items-start">
      <Field label={label} hint={hintFor(value.mode)} className="grow">
        <Select
          value={value.mode}
          onChange={(e) => onChange(withMode(value, e.target.value as PaginationMode))}
        >
          {optionsFor(value.mode).map((mode) => (
            <option key={mode.value} value={mode.value}>
              {mode.label}
            </option>
          ))}
        </Select>
      </Field>
      {/* Only the counting mode has a parameter, so the field is absent rather
          than disabled in the other two: a control that cannot apply still
          reads as a setting somebody has to consider. */}
      {(value.mode === "page_param" || value.mode === "page_until_short") && (
        <Field
          label="Parameter"
          hint="The query parameter to increment."
          className="fold:w-[160px] fold:shrink-0"
        >
          <TextInput
            value={value.param ?? ""}
            placeholder="page"
            onChange={(e) => onChange({ ...value, param: e.target.value })}
          />
        </Field>
      )}
      {/* Required rather than optional: this mode knows nothing else, and a
          size that disagrees with the one in the address stops the walk a page
          early on every Feed. */}
      {value.mode === "page_until_short" && (
        <Field
          label="Page size"
          hint="How many records a full page holds."
          className="fold:w-[160px] fold:shrink-0"
        >
          <TextInput
            inputMode="numeric"
            value={value.size == null ? "" : String(value.size)}
            placeholder="100"
            onChange={(e) =>
              onChange({ ...value, size: e.target.value ? Number(e.target.value) : null })
            }
          />
        </Field>
      )}
    </div>
  );
}

/** The in-row form, for the Feed list where each row carries its own choice. */
export function PaginationChoiceCompact({
  value,
  onChange,
  urlLabel,
}: {
  value: Pagination;
  onChange: (value: Pagination) => void;
  /** Which Feed this row is, for assistive tech — the visible label is the URL. */
  urlLabel: string;
}) {
  return (
    <div className="flex grow items-center gap-xs">
      {/* The mode gives up width so that the parameter sits beside it rather
          than under it: the two are one setting, and a row narrow enough to
          wrap them apart makes it read as two. */}
      <SelectCompact
        className="min-w-0 flex-1"
        aria-label={`Pagination for ${urlLabel}`}
        value={value.mode}
        onChange={(e) => onChange(withMode(value, e.target.value as PaginationMode))}
      >
        {optionsFor(value.mode).map((mode) => (
          <option key={mode.value} value={mode.value}>
            {mode.label}
          </option>
        ))}
      </SelectCompact>
      {(value.mode === "page_param" || value.mode === "page_until_short") && (
        <TextInputCompact
          aria-label={`Page parameter for ${urlLabel}`}
          width="w-[96px] shrink-0"
          value={value.param ?? ""}
          placeholder="page"
          onChange={(e) => onChange({ ...value, param: e.target.value })}
        />
      )}
      {value.mode === "page_until_short" && (
        <TextInputCompact
          aria-label={`Page size for ${urlLabel}`}
          width="w-[80px] shrink-0"
          inputMode="numeric"
          value={value.size == null ? "" : String(value.size)}
          placeholder="100"
          onChange={(e) =>
            onChange({ ...value, size: e.target.value ? Number(e.target.value) : null })
          }
        />
      )}
    </div>
  );
}

/**
 * Switching mode keeps the parameter that was typed, so flipping to `none` and
 * back does not lose it. The API drops it from what it stores for the modes that
 * cannot use one, so nothing but this form ever sees it.
 */
function withMode(value: Pagination, mode: PaginationMode): Pagination {
  return { mode, param: value.param ?? null, size: value.size ?? null };
}

function hintFor(mode: PaginationMode): string {
  return HINTS[mode] ?? "The Fetcher does not support this mode; it will not be honoured.";
}

/** One line describing a declared choice, for the read-only view of a Feed. */
export function describePagination(value: Pagination): string | null {
  if (value.mode === "auto") return null;
  if (value.mode === "none") return "one page only";
  if (value.mode === "page_param") return `counts pages on ?${value.param || "page"}`;
  return `${value.mode} (not a pagination mode the portal supports)`;
}
